import {
  Box,
  Center,
  Divider,
  Heading,
  Text,
  Stack,
  useToast,
} from "@chakra-ui/react";
import ErrorText from "components/API/ErrorText";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "redux/store";
import InvoiceQr from "./InvoiceQr";
import InvoiceTimer from "./InvoiceTimer";
import InvoiceDescription from "./InvoiceDescription";
import InvoiceForm from "./InvoiceForm";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import InvoicePaymentSelector from "./InvoicePaymentSelector";
import { useEffect, useState } from "react";
import { apiAxios } from "utils/axios";
import { trackEcommercePurchase } from "utils/analytics";
import { InvoiceStatus } from "types/invoice";

// localStorage key holding invoice ids for which a `purchase` event has already
// been pushed — prevents double-counting revenue across page reloads.
const TRACKED_INVOICES_KEY = "ym_ecom_tracked_invoices";

const InvoiceDetails = () => {
  const apiKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const toast = useToast();

  const pathname = useLocation().pathname;
  const splittedPathname = pathname.split("/");
  const invoiceIdByPath = splittedPathname[splittedPathname.length - 1];

  const [updatingInvoice, setUpdatingInvoice] = useState(false);
  // Once the purchase is tracked we stop polling (pass null pollInterval).
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  const { data, error, loading } = useApiRequest(
    {
      method: RequestMethod.GET,
      name: RequestName.GET_INVOICE,
      apiToken: apiKey,
      getInvoiceParams: {
        invoiceId: invoiceIdByPath,
      },
    },
    // Poll the invoice status every 30s so a successful payment is detected
    // without a manual reload; stop once we've already tracked the purchase.
    purchaseTracked ? null : 30000
  );
  
  const handlePaymentMethodSelect = async (currency: string, network: string) => {
    setUpdatingInvoice(true);
    
    try {
      const response = await apiAxios({
        method: RequestMethod.POST,
        url: `/update_invoice`,
        headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
        data: {
          invoice_id: invoiceIdByPath,
          network: network,
          coin: currency,
        },
      });

      if (response.data?.status === "success") {
        toast({
          title: "Payment method selected",
          description: "Invoice has been updated successfully",
          status: "success",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        
        // Reload page to refresh data
        window.location.reload();
      } else {
        throw new Error("Failed to update invoice");
      }
    } catch (err) {
      console.error("Error updating invoice:", err);
      toast({
        title: "Error updating invoice",
        description: "Please try again or select another payment method",
        status: "error",
        position: "top",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingInvoice(false);
    }
  };

  const invoiceData: any = data?.data || {};

  // Log invoice data for debugging
  if (data?.status === "success" && invoiceData) {
    console.log("=== Invoice Data from API ===");
    console.log("Full invoice data:", JSON.stringify(invoiceData, null, 2));
    console.log("amount_usd:", invoiceData.amount_usd);
    console.log("client_amount:", invoiceData.client_amount);
    console.log("amount:", invoiceData.amount);
    console.log("description:", invoiceData.description);
    console.log("=============================");
  }

  // Check if invoice has currency and network set
  const hasPaymentMethod = invoiceData?.coin && invoiceData?.network;

  // Basic invoice fields
  const invoiceId =
    invoiceData.ugate_invoice_id ||
    invoiceData.invoice_id ||
    invoiceIdByPath;

  const orderId = invoiceData.invoice_id || invoiceData.id || null;
  const transactionId =
    invoiceData.txid ||
    invoiceData.transaction_id ||
    invoiceData.tx_id ||
    null;

  // Show Order ID only if it differs from Invoice ID
  const showOrderId = orderId && orderId !== invoiceId;

  const statusLabel: string =
    invoiceData.invoice_status ||
    invoiceData.status ||
    InvoiceStatus.WAITING_METHOD;

  // Fiat payment information
  // Priority: amount_usd > amount (USD) > parse from description
  let fiatCurrency = invoiceData.currency_code || "USD";
  let fiatAmount = "";

  // 1) amount_usd (if API starts returning it)
  if (invoiceData.amount_usd !== undefined && invoiceData.amount_usd !== null) {
    const amountValue =
      typeof invoiceData.amount_usd === "number"
        ? invoiceData.amount_usd
        : parseFloat(invoiceData.amount_usd);
    if (!isNaN(amountValue) && amountValue > 0) {
      fiatAmount = amountValue.toFixed(2);
      fiatCurrency = "USD";
    }
  }

  // 2) amount — exactly 20 (fiat), don't touch client_amount (that's crypto)
  if (!fiatAmount && invoiceData.amount !== undefined && invoiceData.amount !== null) {
    const amountValue =
      typeof invoiceData.amount === "number"
        ? invoiceData.amount
        : parseFloat(invoiceData.amount);
    if (!isNaN(amountValue) && amountValue > 0) {
      fiatAmount = amountValue.toFixed(2);
      // Currency — from currency_code, default USD
      fiatCurrency = invoiceData.currency_code || "USD";
    }
  }

  // 3) Parse from description (e.g. "C439 Top-up 20.0 USD") — as fallback
  if (!fiatAmount && invoiceData.description && typeof invoiceData.description === "string") {
    const descMatch = invoiceData.description.match(/(\d+\.?\d*)\s*(USD|EUR|RUB|BTC|ETH|LTC|TRX)/i);
    if (descMatch) {
      const parsedAmount = parseFloat(descMatch[1]);
      if (!isNaN(parsedAmount) && parsedAmount > 0) {
        fiatAmount = parsedAmount.toFixed(2);
        fiatCurrency = descMatch[2].toUpperCase();
      }
    }
  }

  // Log parsed amount result
  if (data?.status === "success") {
    console.log("=== Parsed Amount Result ===");
    console.log("fiatAmount:", fiatAmount);
    console.log("fiatCurrency:", fiatCurrency);
    console.log("===========================");
  }

  // Fire a single ecommerce `purchase` event once the invoice is paid.
  // Deduped per-mount (purchaseTracked) and across reloads (localStorage).
  const purchaseId = orderId || invoiceId;
  useEffect(() => {
    if (statusLabel !== InvoiceStatus.SUCCESS || !purchaseId || purchaseTracked) return;

    let tracked: string[] = [];
    try {
      tracked = JSON.parse(localStorage.getItem(TRACKED_INVOICES_KEY) || "[]");
    } catch {
      tracked = [];
    }

    // Already pushed for this invoice on a previous visit — just stop polling.
    if (tracked.includes(String(purchaseId))) {
      setPurchaseTracked(true);
      return;
    }

    const revenue = parseFloat(fiatAmount);
    if (!fiatAmount || isNaN(revenue) || revenue <= 0) return;

    trackEcommercePurchase({
      transactionId: String(purchaseId),
      revenue,
      currency: fiatCurrency || "USD",
      products: [
        {
          id: "balance_topup",
          name: "Balance top-up",
          category: "Balance",
          price: revenue,
          quantity: 1,
        },
      ],
    });

    try {
      localStorage.setItem(
        TRACKED_INVOICES_KEY,
        JSON.stringify([...tracked, String(purchaseId)])
      );
    } catch {
      // ignore storage write failures — the per-mount guard still prevents dupes
    }
    setPurchaseTracked(true);
  }, [statusLabel, purchaseId, fiatAmount, fiatCurrency, purchaseTracked]);

  return (
    <Center>
      <BoxShadowContainer>
        <Box maxW="800px" w="100%">
        {updatingInvoice ? (
          <Center>
            <LoaderSpinner />
          </Center>
        ) : data?.status === "success" ? (
          <>
            {/* Page header / invoice name */}
            <Heading size="lg" mb={6}>
              <InvoiceDescription description={invoiceData.description} />
            </Heading>

            {/* Status */}
            <Box mb={6}>
              <Text
                fontSize="xs"
                textTransform="uppercase"
                color="status.info"
                letterSpacing="0.1em"
                fontWeight="bold"
              >
                {statusLabel}
              </Text>
            </Box>

            {/* Basic information */}
            <Box mb={6} p={4} bg="bg.surface" borderRadius="lg" borderWidth="1px" borderColor="border.subtle">
              <Text fontWeight="bold" fontSize="md" mb={4} color="text.primary">
                Basic Information
              </Text>
              <Stack spacing={3} fontSize="sm">
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                  <Text fontWeight="semibold" color="text.secondary">Invoice ID:</Text>
                  <Text color="text.primary" fontFamily="mono" fontWeight="medium">{invoiceId || "—"}</Text>
                </Box>
                {showOrderId && (
                  <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                    <Text fontWeight="semibold" color="text.secondary">Order ID:</Text>
                    <Text color="text.primary" fontFamily="mono" fontWeight="medium">{orderId}</Text>
                  </Box>
                )}
                {transactionId && (
                  <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                    <Text fontWeight="semibold" color="text.secondary">Transaction ID:</Text>
                    <Text color="text.primary" fontFamily="mono" fontSize="xs" fontWeight="medium">{transactionId}</Text>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Payment information */}
            <Box mb={6} p={4} bg="bg.surface" borderRadius="lg" borderWidth="1px" borderColor="border.subtle">
              <Text fontWeight="bold" fontSize="md" mb={4} color="text.primary">
                Payment Information
              </Text>
              <Stack spacing={3} fontSize="sm">
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                  <Text fontWeight="semibold" color="text.secondary">Currency:</Text>
                  <Text color="text.primary" fontWeight="medium">{fiatCurrency || "USD"}</Text>
                </Box>
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                  <Text fontWeight="semibold" color="text.secondary">Amount:</Text>
                  <Text color="text.primary" fontWeight="bold" fontSize="md">
                    {fiatAmount || "—"}
                  </Text>
                </Box>
                {hasPaymentMethod && (
                  <>
                    <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                      <Text fontWeight="semibold" color="text.secondary">Cryptocurrency:</Text>
                      <Text color="text.primary" textTransform="uppercase" fontWeight="bold">{invoiceData.coin}</Text>
                    </Box>
                    <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="border.subtle">
                      <Text fontWeight="semibold" color="text.secondary">Network:</Text>
                      <Text color="text.primary" fontWeight="medium">{invoiceData.network}</Text>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
            
            {!hasPaymentMethod ? (
              // Show currency selector if not selected
              <Box mt={2}>
                <InvoicePaymentSelector onSelect={handlePaymentMethodSelect} />
              </Box>
            ) : (
              // Show invoice details if currency is selected
              <>
                <Center mt={6}>
                  <InvoiceQr address={invoiceData.wallet_address} />
                </Center>
                <Box mt={6} position="relative" textAlign="center">
                  <Box bg="bg.default" px={3} zIndex={2} position="absolute" left="50%" transform="translate(-50%, -50%)">or</Box>
                  <Divider my={5} />
                  <Box>
                    <InvoiceForm 
                      network={invoiceData.network}
                      coin={invoiceData.coin}
                      amount={invoiceData.client_amount} 
                      address={invoiceData.wallet_address}
                    />
                    <Box mt={5}>
                      <InvoiceTimer
                        created={invoiceData.created_at}
                        expires={invoiceData.payment_due_date}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </>
        ) : loading ? (
          <Center>
            <LoaderSpinner />
          </Center>
        ) : (
          error && <ErrorText />
        )}
        </Box>
      </BoxShadowContainer>
    </Center>
  );
};

export default InvoiceDetails;
